import React from 'react';
import { Alert, Typography } from 'antd';
import type { OperationLog } from '@/api/requirementSync';

const { Text } = Typography;

const FIELD_LABELS: Record<string, string> = {
  reqId: '需求ID',
  owner: '责任人',
  description: '需求描述',
  planStage: '交付阶段',
  deliverables: '交付物',
  validationMethod: '验证方法',
  validationCriteria: '验证准则',
  priority: '优先级',
  attachment: '设计文档',
  hardware: '硬件',
  reqSource: '需求来源',
  catagory: '分类',
  catagory2: '二级分类',
  reqStatus: '状态',
  tag: '标签',
  subcatagory: '子类别',
  version: '版本',
  synced_ids: '同步ID列表',
  req_ids: '需求ID列表',
  deleted_ids: '删除ID列表',
  replaced_reqs: '替换需求',
};

const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined) return '(空)';
  if (typeof value === 'string') return value || '(空)';
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '(空)';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const computeChangedFields = (
  before: Record<string, unknown>,
  after: Record<string, unknown>,
) => {
  const excluded = new Set(['id', 'version']);
  const allFields = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changes: { field: string; before: string; after: string }[] = [];
  allFields.forEach((field) => {
    if (excluded.has(field)) return;
    const bv = before[field];
    const av = after[field];
    if (JSON.stringify(bv) !== JSON.stringify(av)) {
      changes.push({
        field,
        before: formatFieldValue(bv),
        after: formatFieldValue(av),
      });
    }
  });
  return changes;
};

interface Props {
  log: OperationLog;
}

const FieldChangesView: React.FC<Props> = ({ log }) => {
  const { before_data, after_data } = log;

  if (before_data && after_data) {
    const changes = computeChangedFields(
      before_data as Record<string, unknown>,
      after_data as Record<string, unknown>,
    );
    if (changes.length === 0) {
      return <Alert title="无字段变更" type="info" showIcon />;
    }
    return (
      <div style={{ padding: '0 8px' }}>
        {changes.map(({ field, before, after }) => (
          <div key={field} style={{ marginBottom: 8 }}>
            <Text strong>{FIELD_LABELS[field] || field}</Text>
            <div style={{ marginLeft: 16, marginTop: 4 }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>变更前: </Text>
                <Text delete type="danger">{before}</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>变更后: </Text>
                <Text strong type="success">{after}</Text>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!before_data && after_data) {
    const entries = Object.entries(after_data as Record<string, unknown>).filter(
      ([k]) => !['id', 'version'].includes(k),
    );
    return (
      <div>
        <Alert title="创建操作" type="success" showIcon style={{ marginBottom: 8 }} />
        {entries.map(([key, val]) => (
          <div key={key} style={{ marginLeft: 16, marginBottom: 4 }}>
            <Text strong style={{ fontSize: 12 }}>{FIELD_LABELS[key] || key}: </Text>
            <Text type="success">{formatFieldValue(val)}</Text>
          </div>
        ))}
      </div>
    );
  }

  if (before_data && !after_data) {
    const entries = Object.entries(before_data as Record<string, unknown>).filter(
      ([k]) => !['id', 'version'].includes(k),
    );
    return (
      <div>
        <Alert title="删除操作" type="warning" showIcon style={{ marginBottom: 8 }} />
        {entries.map(([key, val]) => (
          <div key={key} style={{ marginLeft: 16, marginBottom: 4 }}>
            <Text strong style={{ fontSize: 12 }}>{FIELD_LABELS[key] || key}: </Text>
            <Text delete type="danger">{formatFieldValue(val)}</Text>
          </div>
        ))}
      </div>
    );
  }

  return <Alert title="无详细变更信息" type="info" showIcon />;
};

export default FieldChangesView;
